import { Component, Input } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { DataFormModel } from '../../models/data-form-model.model';
import { Faq } from '../../models/faq.model';
import { FaqGroup } from '../../models/faq-group.model';

import { FaqService, IFaqRequest } from '../../services/faq.service';
import { UtilService } from '../../services/util.service';

@Component({
  selector: 'app-faq-modal',
  templateUrl: './faq-modal.component.html',
  styleUrl: './faq-modal.component.scss'
})
export class FaqModalComponent {

  @Input() faqGroup!: FaqGroup;
  @Input() faq?: Faq;

  /* FAQS.question es VARCHAR(65) y el backend NO valida el largo: si se pasa
     revienta con 500. El tope se corta aquí. answer es TEXT, sin límite. */
  public readonly questionMaxLength: number = 65;

  public dataForm: DataFormModel = {
    question: {
      value: '',
      type: 'text',
      label: 'Pregunta',
      placeholder: '¿Cómo genero un pase para una visita?',
      requiredMessage: 'Por favor, ingrese la pregunta',
      required: true,
      validators: [
        Validators.required,
        Validators.maxLength(this.questionMaxLength)
      ]
    },
    answer: {
      value: '',
      type: 'textarea',
      label: 'Respuesta',
      placeholder: 'Explica paso a paso lo que debe hacer el residente.',
      requiredMessage: 'Por favor, ingrese la respuesta',
      required: true,
      validators: [Validators.required]
    },
    videoUrl: {
      value: '',
      type: 'url',
      label: 'Video de apoyo (opcional)',
      placeholder: 'https://...',
      required: false,
      validators: []
    },
    status: {
      /* Valor por defecto de un alta, no autorrelleno: en edición se sobrescribe
         con el dato real en loadData(). */
      value: true,
      type: 'switch',
      label: 'Pregunta activa',
      required: false,
      validators: []
    },
  };

  public formFaq!: FormGroup;
  public loading: boolean = false;
  public submitted: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    public faqService: FaqService,
    public utilService: UtilService,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  get f() {
    return this.formFaq.controls;
  }

  /*
  ██████╗ ██╗   ██╗██╗██╗     ██████╗ ██████╗  █████╗ ██╗   ██╗██╗      ██████╗  █████╗ ██████╗
  ██╔══██╗██║   ██║██║██║     ██╔══██╗██╔══██╗██╔══██╗╚██╗ ██╔╝██║     ██╔═══██╗██╔══██╗██╔══██╗
  ██████╔╝██║   ██║██║██║     ██║  ██║██████╔╝███████║ ╚████╔╝ ██║     ██║   ██║███████║██║  ██║
  ██╔══██╗██║   ██║██║██║     ██║  ██║██╔═══╝ ██╔══██║  ╚██╔╝  ██║     ██║   ██║██╔══██║██║  ██║
  ██████╔╝╚██████╔╝██║███████╗██████╔╝██║     ██║  ██║   ██║   ███████╗╚██████╔╝██║  ██║██████╔╝
  ╚═════╝  ╚═════╝ ╚═╝╚══════╝╚═════╝ ╚═╝     ╚═╝  ╚═╝   ╚═╝   ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝

  */
  buildPayload(): IFaqRequest {

    const videoUrl = `${this.formFaq.value.videoUrl ?? ''}`.trim();

    return {
      question: `${this.formFaq.value.question ?? ''}`.trim(),
      answer: `${this.formFaq.value.answer ?? ''}`.trim(),
      /* videoUrl SIEMPRE viaja: si el PUT lo omite, el backend lo pone en null
         y borra el video que la pregunta ya tenía. */
      videoUrl: videoUrl.length > 0 ? videoUrl : null,
      status: this.formFaq.value.status === true
    };

  }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async create() {

    const flag = await this.faqService.create(
      this.faqGroup,
      this.buildPayload()
    );

    if(flag) {
      this.activeModal.close();
    }

  }

  /*
  ██╗      ██████╗  █████╗ ██████╗ ██████╗  █████╗ ████████╗ █████╗
  ██║     ██╔═══██╗██╔══██╗██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
  ██║     ██║   ██║███████║██║  ██║██║  ██║███████║   ██║   ███████║
  ██║     ██║   ██║██╔══██║██║  ██║██║  ██║██╔══██║   ██║   ██╔══██║
  ███████╗╚██████╔╝██║  ██║██████╔╝██████╔╝██║  ██║   ██║   ██║  ██║
  ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

  */
  async loadData() {
    this.loading = true;

    /* Solo se precarga cuando el modal se abrió SOBRE una pregunta existente.
       En alta el formulario queda vacío: nunca se autorrellena nada que pueda
       sobrescribir un dato ya capturado. */
    if(this.faq) {

      this.dataForm['question'].value = this.faq.question;
      this.dataForm['answer'].value = this.faq.answer;
      this.dataForm['videoUrl'].value = this.faq.videoUrl ?? '';
      this.dataForm['status'].value = this.faq.status;

    }

    this.formFaq = this.utilService.buildForm(this.dataForm);

    this.loading = false;
  }

  /*
   ██████╗ ███╗   ██╗███████╗██╗   ██╗██████╗ ███╗   ███╗██╗████████╗
  ██╔═══██╗████╗  ██║██╔════╝██║   ██║██╔══██╗████╗ ████║██║╚══██╔══╝
  ██║   ██║██╔██╗ ██║███████╗██║   ██║██████╔╝██╔████╔██║██║   ██║
  ██║   ██║██║╚██╗██║╚════██║██║   ██║██╔══██╗██║╚██╔╝██║██║   ██║
  ╚██████╔╝██║ ╚████║███████║╚██████╔╝██████╔╝██║ ╚═╝ ██║██║   ██║
   ╚═════╝ ╚═╝  ╚═══╝╚══════╝ ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝   ╚═╝

  */
  async onSubmit() {

    /* El botón de envío SIEMPRE está habilitado: la validación corre aquí y el
       error se pinta debajo del campo que lo provocó. */
    this.submitted = true;

    if(this.formFaq.invalid) {
      return;
    }

    if(this.faq) {
      await this.update();
    } else {
      await this.create();
    }

  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update() {

    /* El PUT no es parcial: question, answer, videoUrl y status van SIEMPRE. */
    const flag = await this.faqService.update(
      this.faqGroup,
      this.faq!.faqId,
      this.buildPayload()
    );

    if(flag) {
      this.activeModal.close();
    }

  }

}
